import { ApiResponse } from "../../services/base/apiResponse";
import { AdminAppStudentPaymentsResponse, StudentInfoAdminResponse, StudentSemesterAttestationResponse } from "../../models/dtos";
import { studentsApi } from "../../services/students/studentsApi";
import { Button } from "primereact/button";
import { paymentsApi } from "../../services/payments/paymentsApi";
import { useState } from "react";
import { InputText } from "primereact/inputtext";
import { Card } from "primereact/card";
import SemesterAttestationForm from "./SemesterAttestationForm";
import PaymentsTable from "./PaymentsTable";
import AttestationsTable from "./AttestationsTable";

interface StudentPaymentProps {
  student?: ApiResponse<StudentInfoAdminResponse>;
}

const PaymentsInformation = ({ student }: StudentPaymentProps) => {
  const { data: payments, refetch: refetchPayments } = studentsApi.useGetStudentPaymentsQuery(student?.data?.studentId ?? 0);
  const { data: studentAttestations, refetch: refetchAttestations } = studentsApi.useGetStudentSemesterAttestationsQuery(
    student?.data?.studentId ?? 0,
  );

  const [addPayment] = paymentsApi.useAddPaymentMutation();
  const [updatePayment] = paymentsApi.useUpdatePaymentMutation();
  const [deletePayment] = paymentsApi.useDeletePaymentMutation();
  const [deleteAttestation] = studentsApi.useDeleteStudentSemesterAttestationMutation();
  const [editingAttestation, setEditingAttestation] = useState<StudentSemesterAttestationResponse | null>(null);
  const [amount, setAmount] = useState<number>(student?.data?.feeAmount ?? 0);

  const [nextSemester, { isLoading: isNextSemesterLoading }] = studentsApi.useNextSemesterMutation();

  const handleAddPayment = () => {
    addPayment({
      studentId: student?.data?.studentId!,
      amount: amount,
    }).then(() => {
      refetchPayments();
    });
  };

  const handleUpdatePayment = async (payment: AdminAppStudentPaymentsResponse) => {
    try {
      await updatePayment({
        paymentId: payment.paymentId,
        amount: payment.amount ?? 0,
        startedOn: payment.startedOn ?? ""
      }).unwrap();
      refetchPayments();
    } catch (error) {
      console.error('Failed to update payment:', error);
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    try {
      await deletePayment(paymentId).unwrap();
      refetchPayments();
    } catch (error) {
      console.error('Failed to delete payment:', error);
    }
  };

  const handleEditAttestation = (attestation: StudentSemesterAttestationResponse) => {
    setEditingAttestation(attestation);
  };

  const handleDeleteAttestation = async (attestationId: number) => {
    try {
      await deleteAttestation(attestationId).unwrap();
      refetchAttestations();
    } catch (error) {
      console.error('Failed to delete attestation:', error);
    }
  };

  const handleAttestationSuccess = () => {
    setEditingAttestation(null);
    refetchAttestations();
  };

  return (
    <div className="card">
      <Card className="mb-4">
        <div className="flex flex-column">
          <div className="p-1 mb-4">
            <h4>Записване за следващ семестър</h4>
            <p>Натискането на бутона ще премести студента в следващ семестър и ще изпрати събитието към НАЦИД.</p>
          </div>
          <div className="p-1 mb-4">
            <Button
              label="Записване за следващия семестър"
              icon="pi pi-arrow-right"
              className="p-button-sm p-button-outlined"
              loading={isNextSemesterLoading}
              onClick={() => nextSemester({ studentId: student?.data?.studentId! })}
            />
          </div>
        </div>
      </Card>

      <Card className="mb-4">
        <div className="p-1 mb-4">
          <h4>Заверка</h4>
          {student?.data?.studentId && (
            <>
              {editingAttestation ? (
                <SemesterAttestationForm 
                  student={student.data} 
                  onSuccess={handleAttestationSuccess}
                  initialValues={editingAttestation}
                />
              ) : (
                <SemesterAttestationForm 
                  student={student.data} 
                  onSuccess={refetchAttestations}
                />
              )}
            </>
          )}

          <AttestationsTable 
            attestations={studentAttestations?.data || []}
            onEditAttestation={handleEditAttestation}
            onDeleteAttestation={handleDeleteAttestation}
          />
        </div>
      </Card>

      <Card className="mb-4">
        <div className="p-1 mb-4">
          <h4>Плащания</h4>
          <InputText value={amount.toString()} onChange={(e) => setAmount(Number(e.target.value))} />
          <Button
            label="Добави плащане"
            icon="pi pi-plus"
            className="p-button-sm p-button-outlined"
            onClick={handleAddPayment}
          />
        </div>

        {payments && (
          <PaymentsTable
            payments={payments.data}
            onEditPayment={() => {}}
            onDeletePayment={handleDeletePayment}
            onUpdatePayment={handleUpdatePayment}
          />
        )}
      </Card>
    </div>
  );
};

export default PaymentsInformation;
